import { Integration, LocalEditableIntegration } from "../../../Everou/_modelsTS/Integration";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useIntegrationName } from "./useIntegrationName";
import CreatePublicUrlToggle from "../../../Everou/InvitationsModule/CreatePublicUrlToggle";
import { CapsuleButton } from "../../../Everou/_components";
import { useDispatch, useSelector } from "react-redux";
import { MIN_TIME, URL_CONSTANTS } from "../../../Everou/_constants";
import { useError } from "../../../Everou/_hooks/useError";
import { Icons } from "../../../Everou/_assets";
import { PMSIntegrationsCollection } from "../../_stores/PMSIntegrationsCollection";
import { PMSIntegrationsActions } from "../../_actions/PMSIntegrationsActions";
import { CheckInOutFields } from "../../../Everou/IntegrationsModule/components/CheckInOutFields";


type IntegrationInputProps = {
    integration: LocalEditableIntegration;
    onChangeInputs?: (...args: any) => void | any;
    onToggleEditable: (...args: any) => void | any;
    onDelete: (index: number) => void;
    index: number;
}

export function PMSEnsoConnectIntegrationInput({ 
    integration, 
    onDelete, 
    index,
    onToggleEditable 
}: IntegrationInputProps) {

    const { t } = useTranslation();
    const { templateName, nameInput } = useIntegrationName(integration);
    const dispatch = useDispatch();
    
    const integrations = useSelector(PMSIntegrationsCollection.selectorGetItems);

    const baseIntegration = useMemo(() => integration, [integration]);
    const [createPublicUrl, setCreatePublicUrl] = useState(integration.createPublicUrl);
    const { errorMessage, setError, hasError, disableError } = useError();

    const [checkIn, setCheckIn] = useState(integration.check_in ?? MIN_TIME);
    const [checkOut, setCheckOut] = useState(integration.check_out ?? MIN_TIME);

    useEffect(() => { 
        setCreatePublicUrl(integration.createPublicUrl);
        setCheckIn(integration.check_in ?? '00:00');
        setCheckOut(integration.check_out ?? '00:00');
    }, [integration]);

    const enableSendInvitationURLToggle = useMemo(() => {
        return createPublicUrl;
    }, [createPublicUrl]);

    const canSave = useMemo(() => {

        if(hasError) return false;
        
        return (
            !integration.uid ||
            createPublicUrl !== integration.createPublicUrl || 
            checkIn !== integration.check_in ||
            checkOut !== integration.check_out
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        integration.uid,
        createPublicUrl, 
        checkIn, 
        checkOut
    ]);
    

    const onSave = useCallback(async () => {
        const url = URL_CONSTANTS.URL_API + URL_CONSTANTS.INTEGRATION_ENSO_CONNECT_HANDLER;

        const editedIntegration: Integration = {
            name: templateName,
            company: integration.company,
            createPublicUrl,
            uid: integration.uid,
            check_in: checkIn,
            check_out: checkOut,
            link: url,
        }
        try {
            await dispatch(PMSIntegrationsActions.createIntegration(editedIntegration));
        } catch (err: any) {
            setError(err.message)
        }
    }, [
        templateName,
        createPublicUrl, 
        dispatch, 
        setError, 
        integration.company,
        integration.uid, 
        t, 
        checkIn, 
        checkOut
    ]);

    function handleCopyToClipboard(text) {
        navigator.clipboard.writeText(text);
    }

    return (
        <div className="c-grid c-grid--small w-100 c-hover-250 p-4 rounded-lg">
            {nameInput}
            {integration.apikey && 
                <div className="d-flex justify-content-between align-items-center">
                    Token
                    <div className="ml-2" />
                    <div className="d-flex align-items-center">
                        <CapsuleButton
                            icon={Icons.copy}
                            onClick={() => handleCopyToClipboard(integration.apikey)}
                            size={'SMALL'}
                        />
                        <div className="ml-2" />
                        <div className="bg-light border rounded px-1">
                            {integration.apikey}
                        </div>
                    </div>
                </div>
            }
            {integration.apikey &&
                <div>
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                            Webhook URL
                            <div className="ml-2" />
                            <CapsuleButton
                                icon={Icons.copy}
                                onClick={() => handleCopyToClipboard(integration.link)}
                                size={'SMALL'}
                            />
                            
                        </div>
                    </div>
                    <div className="mt-2" />
                    <div className="bg-light border rounded px-1">
                        {integration.link}
                    </div>
                </div>
            }
            <div
                style={{
                    display: "grid",
                    gap: "12px",
                    gridTemplateColumns: "1fr 1fr" }}
                className="col-span-full d-flex justify-content-between"
            >
                <CreatePublicUrlToggle
                    boldedName={false}
                    initialState={createPublicUrl}
                    readOnly={!integration.editable}
                    onChange={setCreatePublicUrl}
                />
            </div>
            <CheckInOutFields
                checkIn={checkIn}
                checkOut={checkOut}
                onChangeCheckIn={setCheckIn}
                onChangeCheckOut={setCheckOut}
                readOnly={!integration.editable}
            />
            <div className="col-span-full mt-4 d-flex justify-content-between align-items-center">
                <div className="d-flex">
                    {integration.uid &&
                        <CapsuleButton
                            size="SMALL"
                            classNameExtra="mr-2"
                            style={!integration.editable ? 'PRIMARY' : 'INFO'}
                            onClick={() => onToggleEditable(index)}
                            text={t(!integration.editable ?  "global_edit" : 'global_cancel')}
                        />
                    }
                    {integration.uid &&
                        <CapsuleButton
                            onClick={() => onDelete(index)}
                            classNameExtra="mr-2"
                            // eslint-disable-next-line react/style-prop-object
                            style="DANGER"
                            size="SMALL"
                            icon={Icons.trash}
                        />
                    }
                </div>
                <CapsuleButton
                    size="SMALL"
                    isEnabled={canSave}
                    onClick={onSave}
                    text={t("global_save")}
                />
            </div>
            {errorMessage && (
                <div className="text-danger mr-2">{errorMessage}</div>
            )}
        </div>
    );
}